import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, InputAdornment, Box, Typography, CircularProgress } from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { useHstAccessEditor } from '../../../../hooks/useHstAccessEditor';
import { searchGraphUsers, GraphUser } from '../../../../../../utils/MsGraphApiCall';
import { useFeatureFlag } from '../../../../../../utils/featureFlags';
import { EditorMode } from '../../../../types/editorTypes';

interface Props {
  mode: EditorMode;
}

// FR-002: prefill the HST identity from the selected directory person. The HST user ID is usually
// the person's email, so it starts as the email and the requester can change it; any previous
// login is replaced, so it is never kept with the new person's name and email. The email falls
// back to the UPN, matching mobile's users/search (Mail ?? UserPrincipalName).
const identityFromPerson = (person: GraphUser) => {
  const email = person.mail || person.userPrincipalName || '';
  return {
    user: person.displayName,
    hstUserId: email,
    firstName: person.givenName ?? '',
    lastName: person.surname ?? '',
    email,
  };
};

const clearedIdentity = { user: '', hstUserId: '', firstName: '', lastName: '', email: '' };

const legacyUpdate = (person: GraphUser | null) => ({ user: person ? person.displayName : '' });

const provisioningUpdate = (person: GraphUser | null) => (person ? identityFromPerson(person) : clearedIdentity);

export const HstUserIdEditor: React.FC<Props> = ({ mode }) => {
  const { request, updateRequest } = useHstAccessEditor(mode);
  const provisioningEnabled = useFeatureFlag('HstProvisioning');

  const [people, setPeople] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<GraphUser | null>(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (request?.user && !selectedPerson) {
      const mockPerson: GraphUser = {
        id: '',
        displayName: request.user,
        mail: '',
        jobTitle: '',
        department: '',
        userPrincipalName: '',
        accountEnabled: true
      };
      setSelectedPerson(mockPerson);
    }
  }, [request, selectedPerson]);

  const searchPeople = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setPeople([]);
      return;
    }
    setLoading(true);
    try {
      const results = await searchGraphUsers(searchTerm);
      setPeople(results);
    } catch (error) {
      console.error('Error searching people:', error);
      setPeople([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchPeople(inputValue);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handlePersonSelect = (person: GraphUser | null) => {
    setSelectedPerson(person);
    const toUpdate = provisioningEnabled ? provisioningUpdate : legacyUpdate;
    updateRequest(toUpdate(person));
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Autocomplete
        options={people}
        loading={loading}
        value={selectedPerson}
        onChange={(_, newValue) => handlePersonSelect(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        getOptionLabel={(option) => option.displayName}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Person for HST Access"
            required
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline sx={{ color: '#64748b', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box>
              <Typography variant="body2" fontWeight={500}>{option.displayName}</Typography>
              <Typography variant="caption" color="text.secondary">{option.mail}</Typography>
              {option.jobTitle && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {option.jobTitle} {option.department && `• ${option.department}`}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        noOptionsText={inputValue.length < 2 ? "Type at least 2 characters to search" : "No people found"}
      />
    </Box>
  );
};

export default HstUserIdEditor;
